import React, { useState, useEffect } from 'react';
import {
    Input,
    Form,
    FormGroup,
    Label,
    Button,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Table,
    UncontrolledCollapse,
    Row,
    Col,
} from 'reactstrap';
import classes from './ConsentManager.module.scss';
import { IconChevronDown } from '@tabler/icons-react';
import { createMarkup } from '../../shared/utility';
import Cookies from 'js-cookie';
import axios from '../../axios-base';

const ConsentManager = ({ pages, consentCallback }) => {
    const [activeTab, setActiveTab] = useState('1');
    const [switchTab, setSwitchTab] = useState(false);
    const [statisticCheck, setStatisticCheck] = useState(Cookies.get('bigfoot_statistic-consent') === 'true');
    const [personalizationCheck, setPersonalizationCheck] = useState(
        Cookies.get('bigfoot_personalization-consent') === 'true'
    );
    const [performanceCheck, setPerformanceCheck] = useState(Cookies.get('bigfoot_performance-consent') === 'true');
    const [linkAll, setLinkAll] = useState(false);
    const [clientLoad, setClientLoad] = useState(null);
    // clientAction is sent to the API on consent submission; it is not rendered but must be tracked in state.
    // eslint-disable-next-line no-unused-vars
    const [clientAction, setClientAction] = useState(null);
    const [scrollToHeadline, setScrollToHeadline] = useState(null);

    const tabHandler = (tab, scrollTarget) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            setSwitchTab(true);
        }
    };

    const inputChangeHandler = (event) => {
        const target = event.target;
        const name = target.id;
        const checked = target.checked;

        if (name === 'statisticCheck') setStatisticCheck(checked);
        if (name === 'personalizationCheck') setPersonalizationCheck(checked);
        if (name === 'performanceCheck') setPerformanceCheck(checked);
    };

    const selectionConsentHandler = async () => {
        let date = Date.now();
        setClientAction(date);
        Cookies.set('bigfoot_cookie-consent', true, { expires: 180 });

        const stateToPass = {
            switchTab,
            activeTab,
            statisticCheck,
            personalizationCheck,
            performanceCheck,
            linkAll,
            clientLoad,
            clientAction: date,
        };

        consentCallback(stateToPass);
        axios
            .post('/analytics/', stateToPass)
            .then((response) => {
                console.debug(response);
                window.location.reload(true);
            })
            .catch((error) => {
                console.debug(error);
            });
    };

    const completeConsentHandler = async () => {
        let date = Date.now();
        setStatisticCheck(true);
        setPersonalizationCheck(true);
        setPerformanceCheck(true);
        setLinkAll(true);
        setClientAction(date);

        Cookies.set('bigfoot_cookie-consent', true, { expires: 180 });

        const stateToPass = {
            activeTab,
            switchTab,
            statisticCheck: true,
            personalizationCheck: true,
            performanceCheck: true,
            linkAll: true,
            clientLoad,
            clientAction: date,
        };

        consentCallback(stateToPass);
        axios
            .post('/analytics/', stateToPass)
            .then((response) => {
                console.debug(response);
                window.location.reload(true);
            })
            .catch((error) => {
                console.debug(error);
            });
    };

    const handleLinkClick = (headlineId) => {
        tabHandler('2');
        setScrollToHeadline(headlineId);
    };

    const scrollIfRequired = () => {
        if (scrollToHeadline) {
            setTimeout(() => {
                const headlineElement = document.getElementById(scrollToHeadline);

                if (headlineElement) {
                    headlineElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest',
                    });
                    setScrollToHeadline(null);
                }
            }, 100);
        }
    };

    useEffect(() => {
        let date = Date.now();
        setClientLoad(date);
    }, []);

    useEffect(() => {
        scrollIfRequired();
        // Including scrollIfRequired would cause an infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollToHeadline]);

    let privacyPolicyPage = null;
    let privacyPolicyPageMarkup = null;

    if (pages) {
        privacyPolicyPage = pages.find((page) => page.slug === 'datenschutz');
        if (privacyPolicyPage) privacyPolicyPageMarkup = privacyPolicyPage.body;
    }

    return (
        <React.Fragment>
            <ModalHeader tag="div" className="border-bottom-0">
                <ul className="visually-hidden-focusable mb-3">
                    <li>
                        <a href="#cookie-settings" className="btn btn-link">
                            Zu den Cookie-Einstellungen springen
                        </a>
                    </li>
                    <li>
                        <a href="#privacy-policy" className="btn btn-link ms-2">
                            Zur Privatssphäreerklärung springen
                        </a>
                    </li>
                    <li>
                        <a href="#consent-actions" className="btn btn-link ms-2">
                            Zu den Zustimmungsaktionen springen
                        </a>
                    </li>
                </ul>
                <h4>Ihre Datenschutzpräferenzen</h4>
                <Nav tabs>
                    <NavItem>
                        <NavLink
                            tag="button"
                            className="anl_cmt--tab--cookies"
                            onClick={() => tabHandler('1')}
                            active={activeTab === '1'}
                        >
                            Cookies
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            tag="button"
                            className="anl_cmt--tab--privacy"
                            onClick={() => tabHandler('2')}
                            active={activeTab === '2'}
                        >
                            Datenschutzerklärung
                        </NavLink>
                    </NavItem>
                </Nav>
            </ModalHeader>
            <ModalBody className="pt-1">
                <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                        <p id="cookie-settings">
                            Wir setzen Cookies und andere Technologien ein, um Ihnen das bestmögliche
                            Nutzungs&shy;erlebnis zu gewährleisten. Im Folgenden haben Sie die Möglichkeit Ihre
                            persönlichen Daten&shy;schutz&shy;präferenzen festzulegen.
                        </p>
                        <Form>
                            <FormGroup switch className="form-group mb-2 px-1 rounded bg-light">
                                <Row className="p-2 align-content-center">
                                    <Col id="togglerNecessary">
                                        <Label
                                            for="necessaryCheck"
                                            className="align-content-center mb-0"
                                            id="necessaryCheckLabel"
                                        >
                                            <IconChevronDown size={24} className="text-primary d-inline me-2" />
                                            <h5 className="m-0 text-primary d-inline">
                                                Notwendig
                                                <span className="visually-hidden">
                                                    (Auswählen um die Details aufzuklappen)
                                                </span>
                                            </h5>
                                        </Label>
                                    </Col>
                                    <Col xs="auto">
                                        <Input
                                            name="Notwendig"
                                            id="necessaryCheck"
                                            type="switch"
                                            role="switch"
                                            aria-labelledby="necessaryCheckLabel"
                                            checked
                                            disabled
                                        />
                                    </Col>
                                </Row>
                                <UncontrolledCollapse toggler="#togglerNecessary">
                                    <p className="text-muted mb-1 p-2">
                                        Notwendige Technologien sind zur Funktion unserer Website erforderlich und
                                        können nicht deaktiviert werden. Cookies dieser Kategorie werden als Reaktion
                                        auf von Ihnen getätigten Aktionen gesetzt, die einer Dienstanforderung
                                        entsprechen, wie etwa dem Festlegen Ihrer Datenschutzeinstellungen.
                                    </p>
                                    <Table size="sm" borderless>
                                        <tbody>
                                            <tr className={classes.ConsentManagerTable}>
                                                <th className="w-25" scope="row">
                                                    Dienst
                                                </th>
                                                <td className="px-0">Voting-Wettbewerb</td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Anbieter
                                                </th>
                                                <td className="px-0">Social Value GmbH</td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Daten
                                                </th>
                                                <td className="px-0">Ihre Nutzerpräferenzen</td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Cookies
                                                </th>
                                                <td className="px-0 text-monospace">
                                                    <span className="me-1 badge rounded-pill text-bg-warning">
                                                        bigfoot_cookie-consent
                                                    </span>
                                                    <span className="me-1 badge rounded-pill text-bg-warning">
                                                        bigfoot_functional-consent
                                                    </span>
                                                    <span className="me-1 badge rounded-pill text-bg-warning">
                                                        bigfoot_statistic-consent
                                                    </span>
                                                    <span className="me-1 badge rounded-pill text-bg-warning">
                                                        bigfoot_personalization-consent
                                                    </span>
                                                    <span className="me-1 badge rounded-pill text-bg-warning">
                                                        bigfoot_code-requested
                                                    </span>
                                                    <span className="me-1 badge rounded-pill text-bg-warning">
                                                        bigfoot_tour-projects
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Grundlage
                                                </th>
                                                <td className="px-0">Art. 6 Abs. 1 s. 1 lit. c DSGVO</td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Verarbeitungsort
                                                </th>
                                                <td className="px-0">Paris (Frankreich)</td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Verarbeitung Ihrer Daten
                                                </th>
                                                <td className="px-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleLinkClick('bigfoot-app')}
                                                        className="btn btn-link p-0 anl_cmt--details--bigfoot-app"
                                                    >
                                                        Details zur Weiterverarbeitung Ihrer Daten finden Sie hier
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </UncontrolledCollapse>
                            </FormGroup>

                            <FormGroup switch className="form-group mb-2 px-1 rounded bg-light">
                                <Row className="p-2 align-content-center">
                                    <Col id="togglerStatistic">
                                        <Label
                                            for="statisticCheck"
                                            className="align-content-center mb-0"
                                            id="statisticCheckLabel"
                                        >
                                            <IconChevronDown size={24} className="text-primary d-inline me-2" />
                                            <h5 className="m-0 text-primary d-inline">
                                                Statistik
                                                <span className="visually-hidden">
                                                    (Auswählen um die Details aufzuklappen)
                                                </span>
                                            </h5>
                                        </Label>
                                    </Col>
                                    <Col xs="auto">
                                        <Input
                                            name="statisticCheck"
                                            id="statisticCheck"
                                            onChange={inputChangeHandler}
                                            type="switch"
                                            role="switch"
                                            aria-labelledby="statisticCheckLabel"
                                            checked={statisticCheck}
                                            onClick={() => setStatisticCheck(!statisticCheck)}
                                        />
                                    </Col>
                                </Row>
                                <UncontrolledCollapse toggler="#togglerStatistic">
                                    <p className="mb-1 p-2">
                                        Mit diesen Cookies können wir Besuche zählen und Zugriffs&shy;quellen eruieren,
                                        um die Leistung unserer Website zu ermitteln und zu verbessern.
                                    </p>
                                    <Table size="sm" borderless>
                                        <tbody>
                                            <tr className={classes.ConsentManagerTable}>
                                                <th className="w-25" scope="row">
                                                    Dienst
                                                </th>
                                                <td className="px-0">etracker Webanalyse</td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Anbieter
                                                </th>
                                                <td className="px-0">etracker GmbH</td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Daten
                                                </th>
                                                <td className="px-0">
                                                    Besuchte Seiten, Endgerät, Browser, Betriebssystem, Referrer,
                                                    Nutzerinteraktionen, IP-Adresse
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Cookies
                                                </th>
                                                <td className="px-0 text-monospace">
                                                    <span className="me-1 badge rounded-pill text-bg-warning">
                                                        BT_pdc
                                                    </span>
                                                    <span className="me-1 badge rounded-pill text-bg-warning">
                                                        BT_sdc
                                                    </span>
                                                    <span className="me-1 badge rounded-pill text-bg-warning">
                                                        et_oip
                                                    </span>
                                                    <span className="me-1 badge rounded-pill text-bg-warning">
                                                        _et_coid
                                                    </span>
                                                    <span className="me-1 badge rounded-pill text-bg-warning">
                                                        isSdEnabled
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Grundlage
                                                </th>
                                                <td className="px-0">Einwilligung, Art. 6 Abs. 1 lit. a DSGVO</td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Verarbeitungsort
                                                </th>
                                                <td className="px-0">Europäische Union</td>
                                            </tr>
                                            <tr>
                                                <th className="w-25" scope="row">
                                                    Verarbeitung Ihrer Daten
                                                </th>
                                                <td className="px-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleLinkClick('etracker-web-analyse')}
                                                        className="btn btn-link p-0 anl_cmt--details--etracker-web-analytics"
                                                    >
                                                        Details zur Weiterverarbeitung Ihrer Daten finden Sie hier
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </UncontrolledCollapse>
                            </FormGroup>
                        </Form>
                    </TabPane>
                    <TabPane tabId="2" id="privacy-policy">
                        {privacyPolicyPageMarkup ? (
                            <div dangerouslySetInnerHTML={createMarkup(privacyPolicyPageMarkup)} />
                        ) : (
                            'Loading'
                        )}
                        <div />
                    </TabPane>
                </TabContent>
            </ModalBody>
            <ModalFooter>
                <div id="consent-actions" />
                <Button
                    className="anl_cmt--btn--accept-selection"
                    onClick={selectionConsentHandler}
                    outline
                    color="dark"
                    block
                >
                    Auswahl akzeptieren
                </Button>
                <Button className="anl_cmt--btn--accept-all" onClick={completeConsentHandler} color="secondary" block>
                    Alles akzeptieren
                </Button>
            </ModalFooter>
        </React.Fragment>
    );
};

export default ConsentManager;

Cookies.defaultProps = {
    disableStyles: false,
    hideOnAccept: true,
    acceptOnScroll: false,
    acceptOnScrollPercentage: 25,
    onAccept: () => {},
    cookieName: 'bigfoot_cookie-consent',
    cookieValue: true,
    buttonText: 'OK',
    debug: false,
    expires: 30,
    extraCookieOptions: {},
};
